import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid,
  LinearProgress, Paper, Stack, TextField, Typography,
} from '@mui/material';
import { api, ApiError } from '../api/client';
import type { SubscriptionApplyResult, SubscriptionDetail as SubDetail } from '../api/types';
import { useOperationProgress } from '../hooks/useOperationProgress';
import { PageHeader, fmtUnits, AddressWithCopy } from '../components/common';
import { TxPlanStep, TxProgressDialog, TxResult } from '../components/TxProgressDialog';

/** 청약 신청이 거치는 단계 — 진행 중에도 무슨 작업인지 보여준다(서버 steps 와 key 대응) */
const APPLY_PLAN: TxPlanStep[] = [
  { key: 'verify', label: '적격성 검증 (isVerified)' },
  { key: 'balance', label: '결제 수단 잔액 확인' },
  { key: 'approve', label: '스테이블코인 승인 (approve)' },
  { key: 'deposit', label: '에스크로 예치 (deposit)' },
  { key: 'record', label: '청약 신청 기록' },
];

const STATUS: Record<SubDetail['status'], { label: string; color: 'info' | 'warning' | 'success' | 'error' }> = {
  OPEN: { label: '모집중', color: 'info' },
  CLOSED: { label: '마감', color: 'warning' },
  ISSUED: { label: '발행완료', color: 'success' },
  SETTLED: { label: '정산완료', color: 'success' },
  CANCELLED: { label: '취소(환불)', color: 'error' },
};

export function SubscriptionDetail() {
  const { id = '' } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { data: s, isLoading, error } = useQuery({
    queryKey: ['subscription', id],
    queryFn: () => api.get<SubDetail>(`/subscriptions/${id}`),
  });

  const [applyOpen, setApplyOpen] = useState(false);
  const [qty, setQty] = useState('');
  const [txOpen, setTxOpen] = useState(false);
  const [result, setResult] = useState<TxResult | null>(null);
  const [applyErr, setApplyErr] = useState<string | null>(null);
  const progress = useOperationProgress(); // 진행 중 단계 실시간 폴링

  const apply = useMutation({
    mutationFn: (amount: string) => {
      const opId = progress.start(); // 작업 식별자 발급 + 폴링 시작
      return api.post<SubscriptionApplyResult>(
        `/subscriptions/${id}/apply`, { amount }, { 'X-Operation-Id': opId },
      );
    },
    onMutate: () => { setTxOpen(true); setResult(null); setApplyErr(null); },
    onSuccess: (r) => {
      progress.stop(r.steps); // 응답의 확정 단계로 마무리
      setResult({ status: r.tx.status, txHash: r.tx.txHash, revertReason: r.tx.revertReason });
      void qc.invalidateQueries({ queryKey: ['subscription', id] });
      void qc.invalidateQueries({ queryKey: ['my-deposits'] });
    },
    onError: (e) => {
      progress.stop(); // 폴링으로 받은 마지막 상태를 남겨 실패 지점을 보여준다
      setApplyErr(e instanceof ApiError ? e.message : '청약 신청 실패');
    },
  });

  if (isLoading) return <LinearProgress />;
  if (error || !s) return <Typography color="error">공고를 불러오지 못했습니다.</Typography>;

  const depositedKRWS = Number(s.deposited) / 1e18;
  const target = Number(s.raiseTarget);
  const pct = target > 0 ? Math.min(100, (depositedKRWS / target) * 100) : 0;
  const est = qty ? Number(qty) * Number(s.unitPrice) : 0;

  return (
    <Box>
      <PageHeader crumb="서비스 사용자 · 청약" title={`${s.tokenName}`}
        desc="청약 공고 상세 정보"
        action={<Button onClick={() => nav('/subscriptions')}>← 목록</Button>} />

      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3 }}>
            <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
              <Typography variant="h2">{s.tokenName}</Typography>
              <Typography variant="caption" color="text.secondary">{s.symbol}</Typography>
              <Chip size="small" color={STATUS[s.status].color} label={STATUS[s.status].label} sx={{ fontWeight: 700 }} />
            </Stack>
            <Stack spacing={1.2} divider={<Divider flexItem />}>
              <Row k="단가" v={`${fmtUnits(s.unitPrice, 0)} KRWS`} />
              <Row k="총 수량" v={fmtUnits(s.totalAmount, 0)} />
              <Row k="모집 목표" v={`${fmtUnits(s.raiseTarget, 0)} KRWS`} />
              <Row k="청약 기간" v={`${new Date(s.periodStart).toLocaleString()} ~ ${new Date(s.periodEnd).toLocaleString()}`} />
              <Row k="토큰 컨트랙트" v={s.tokenContract ? <AddressWithCopy address={s.tokenContract} /> : <Typography variant="caption" color="text.secondary">발행 전(청약 성사 시 배포)</Typography>} />
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h2" mb={2}>모집 현황</Typography>
            <Stack spacing={1.5}>
              <Box>
                <Stack direction="row" justifyContent="space-between" mb={0.5}>
                  <Typography variant="body2" color="text.secondary">모집 진행률</Typography>
                  <Typography variant="body2" fontWeight={700}>{pct.toFixed(1)}%</Typography>
                </Stack>
                <LinearProgress variant="determinate" value={pct} sx={{ height: 8, borderRadius: 1 }} />
              </Box>
              <Row k="에스크로 잔액" v={`${fmtUnits(s.deposited)} KRWS`} />
              <Row k="청약자 수" v={`${s.applicantCount}명`} />
            </Stack>
            <Button fullWidth variant="contained" sx={{ mt: 3 }} size="large"
              disabled={s.status !== 'OPEN'}
              onClick={() => { setQty(''); setApplyOpen(true); }}>
              {s.status === 'OPEN' ? '청약 신청' : STATUS[s.status].label}
            </Button>
          </Paper>
        </Grid>
      </Grid>

      {/* 청약 신청 다이얼로그 */}
      <Dialog open={applyOpen} onClose={() => setApplyOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>청약 신청 — {s.tokenName}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">단가 {fmtUnits(s.unitPrice, 0)} KRWS</Typography>
            <TextField label="신청 수량" type="number" value={qty} onChange={(e) => setQty(e.target.value)} autoFocus fullWidth />
            {qty && (
              <Typography variant="body2">
                예상 금액: <b>{est.toLocaleString()}</b> KRWS
                <Typography component="span" variant="caption" color="text.secondary"> (사전 스테이블코인 승인·잔액 필요)</Typography>
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApplyOpen(false)}>취소</Button>
          <Button variant="contained" disabled={!qty || Number(qty) <= 0 || apply.isPending}
            onClick={() => { setApplyOpen(false); apply.mutate(qty); }}>신청</Button>
        </DialogActions>
      </Dialog>

      <TxProgressDialog open={txOpen} title="청약 신청 (에스크로 예치)" pending={apply.isPending}
        result={result} error={applyErr} onClose={() => setTxOpen(false)}
        plan={APPLY_PLAN} steps={progress.steps} />
    </Box>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography color="text.secondary">{k}</Typography>
      <Typography component="div" fontWeight={600}>{v}</Typography>
    </Stack>
  );
}
